<div class="relative flex h-full min-h-0 flex-col">
  @if (store.hasSelectedConversation()) {
    <header class="thread-header border-b border-line bg-panel px-4 py-3">
      <div class="flex min-w-0 items-center justify-between gap-3">
        <div class="flex min-w-0 items-center gap-2">
          <div class="min-w-0">
            <div class="flex min-w-0 items-center gap-2">
              <h1 class="truncate text-base font-semibold text-ink">{{ conversation()!.contact }}</h1>
              <span appBadge [tone]="statusTone(conversation()!.status)">{{ statusLabel(conversation()!.status) }}</span>
            </div>
            <p class="mt-0.5 flex min-w-0 items-center gap-2 text-sm text-muted">
              <app-channel-icon [channel]="conversation()!.channel" variant="header" />
              <span class="truncate">{{ conversation()!.channelIdentity }}</span>
            </p>
          </div>
        </div>

        <app-thread-header-actions />
      </div>
    </header>

    <div #scroller (scroll)="onScroll()" class="thread-scroll flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-4" role="log" aria-live="polite">
      @if (store.hasOlderMessages()) {
        <div class="flex justify-center">
          <button appButton variant="secondary" size="sm" type="button" [disabled]="store.isLoadingOlder()" (click)="store.loadOlderMessages()">
            {{ store.isLoadingOlder() ? 'Carregando...' : 'Carregar mensagens anteriores' }}
          </button>
        </div>
      }
      @if (store.isLoadingMessages()) {
        <app-ui-skeleton-grid [rows]="4" [rowHeight]="56" />
      } @else {
        @for (day of timeline(); track day.dayKey) {
          <section class="day-group">
            <div class="day-separator-row">
              <span class="day-separator">{{ day.label }}</span>
            </div>
            @for (entry of day.entries; track trackEntry(entry)) {
              @if (entry.kind === 'note') {
                <div class="note-marker-row">
                  <button appButton variant="ghost" size="sm" type="button" class="note-marker" (click)="openNote(entry.note.id)" [title]="'Nota interna · ' + entry.note.author + ' · ' + entry.note.time">
                    <ng-icon name="heroPencilSquare" size="13" aria-hidden="true" />
                    <span>Nota interna · {{ entry.note.time }}</span>
                  </button>
                </div>
              } @else {
                @let message = entry.message;
                <article
                  class="message-row"
                  [class.message-row--contact]="message.sender === 'contact'"
                  [class.message-row--agent]="message.sender === 'agent'"
                  [class.message-row--system]="message.sender === 'system'"
                  [class.message-row--end-sequence]="entry.isEndOfSequence"
                >
                  <div class="message-bubble" [attr.id]="'message-' + message.id">
                    @if (canReplyToMessages()) {
                      <button
                        type="button"
                        appUiIconButton
                        variant="ghost"
                        size="xs"
                        class="message-actions-trigger"
                        [ngpMenuTrigger]="messageActionsMenu"
                        ngpMenuTriggerPlacement="bottom-end"
                        [attr.aria-label]="'Ações da mensagem'"
                      >
                        <ng-icon name="heroEllipsisHorizontal" size="13" aria-hidden="true" />
                      </button>
                      <ng-template #messageActionsMenu>
                        <div appMenuPanel class="message-actions-menu" role="menu">
                          <button type="button" appMenuItem role="menuitem" (click)="onReply(message)">
                            Responder
                          </button>
                        </div>
                      </ng-template>
                    }
                    @if (message.quotedReply; as reply) {
                      @if (!reply.unavailable && reply.messageId; as quotedId) {
                        <button type="button" class="message-quoted-reply" (click)="scrollToMessage(quotedId)">
                          <span class="message-quoted-reply__sender">{{ quotedSenderLabel(reply.sender) }}</span>
                          <span class="message-quoted-reply__preview">{{ reply.preview }}</span>
                        </button>
                      } @else {
                        <div class="message-quoted-reply message-quoted-reply--unavailable">
                          <span class="message-quoted-reply__preview">Mensagem indisponível</span>
                        </div>
                      }
                    }
                    @if (message.storyContext; as story) {
                      <div class="message-story-label">
                        <ng-icon [name]="story.kind === 'mention' ? 'heroAtSymbol' : 'heroArrowUturnLeft'" size="12" aria-hidden="true" />
                        <span>{{ story.kind === 'mention' ? 'Mencionou você no story' : 'Respondeu ao seu story' }}</span>
                      </div>
                      @if (story.attachments.length > 0) {
                        <app-message-attachments class="mb-1 block" [attachments]="story.attachments" />
                      } @else if (story.url) {
                        <a class="message-story-reply" [href]="story.url" target="_blank" rel="noopener noreferrer">
                          <ng-icon name="heroArrowTopRightOnSquare" size="12" aria-hidden="true" />
                          <span>Ver story</span>
                        </a>
                      }
                    }
                    @if (message.attachments.length > 0) {
                      <app-message-attachments class="mb-1 block" [attachments]="message.attachments" />
                    }
                    @if (message.body) {
                      <p class="message-body text-sm leading-6">@for (seg of (message.body | linkify); track $index) {@if (seg.href) {<a class="message-link" [href]="seg.href" target="_blank" rel="noopener noreferrer">{{ seg.text }}</a>}@else {<span>{{ seg.text }}</span>}}</p>
                    }
                    @if (message.buttons; as buttons) {
                      <!-- Somente-leitura: o WhatsApp já mostra os botões reais no aparelho do contato.
                           Botão de link vira âncora clicável (abre em nova aba); demais são texto. -->
                      <div class="message-template-buttons">
                        @for (button of buttons; track $index) {
                          @if (button.href) {
                            <a class="message-template-button message-template-button--link" [href]="button.href" target="_blank" rel="noopener noreferrer">{{ button.text }}</a>
                          } @else {
                            <span class="message-template-button">{{ button.text }}</span>
                          }
                        }
                      </div>
                    }
                    <div class="message-meta">
                      <time>{{ message.time }}</time>
                      @if (message.isEcho) {
                        <span appBadge tone="neutral" size="xs" title="Enviada pelo aplicativo do Instagram">via Instagram</span>
                      }
                      @if (message.isCampaign) {
                        <span appBadge tone="neutral" size="xs" title="Enviada automaticamente por um disparo de campanha">Campanha</span>
                      }
                      @if (message.isWebhook) {
                        <span appBadge tone="neutral" size="xs" title="Enviada automaticamente por um gatilho de webhook">Webhook</span>
                      }
                      @if (message.isButtonReply) {
                        <span appBadge tone="neutral" size="xs" title="Resposta por clique em botão, não digitada">Botão</span>
                      }
                      @if (delivery(message); as d) {
                        @if (d.failed) {
                          <button appButton variant="ghost" size="sm" type="button" class="delivery delivery--tomato" [title]="failureTitle(message)" (click)="retry(message)">
                            {{ d.glyph }}
                          </button>
                        } @else {
                          <span class="delivery" [class.delivery--accent]="d.tone === 'accent'" aria-hidden="true">{{ d.glyph }}</span>
                        }
                      }
                      @if (langfuseTraceUrl(message); as traceUrl) {
                        <a
                          class="trace-link"
                          [href]="traceUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Abrir trace no Langfuse"
                          aria-label="Abrir trace no Langfuse em nova aba"
                        >
                          <ng-icon name="heroArrowTopRightOnSquare" size="12" aria-hidden="true" />
                        </a>
                      }
                    </div>
                  </div>
                </article>
              }
            }
          </section>
        } @empty {
          <app-ui-empty-state
            compact
            title="Nenhuma mensagem carregada"
            description="Quando a conversa receber ou enviar mensagens, o histórico aparecerá aqui."
          />
        }
      }
    </div>

    @if (conversation()!.assignedToMe) {
      <footer class="composer bg-app/45 px-5 pb-4">
        @if (!isAtBottom()) {
          <button appUiIconButton class="scroll-bottom shadow-card" variant="soft" size="sm" shape="pill" type="button" aria-label="Ir para a última mensagem" (click)="scrollToBottom()">
            <ng-icon name="heroArrowDown" size="15" aria-hidden="true" />
          </button>
        }
        <app-message-composer />
      </footer>
    } @else {
      <footer class="composer composer--locked bg-app/45 px-5 pb-4">
        @if (!isAtBottom()) {
          <button appUiIconButton class="scroll-bottom shadow-card" variant="soft" size="sm" shape="pill" type="button" aria-label="Ir para a última mensagem" (click)="scrollToBottom()">
            <ng-icon name="heroArrowDown" size="15" aria-hidden="true" />
          </button>
        }
        <div appUiPanelCard class="flex items-center justify-between gap-3 px-3 py-2 text-sm text-muted">
          <span class="flex min-w-0 items-center gap-2">
            <ng-icon name="heroLockClosed" size="16" aria-hidden="true" />
            <span class="truncate">Atribua a conversa para responder.</span>
          </span>
          <button appButton variant="secondary" size="sm" type="button" (click)="assume()">Assumir</button>
        </div>
      </footer>
    }
  } @else {
    <div class="flex h-full items-center justify-center p-6">
      <app-ui-empty-state
        icon="heroInformationCircle"
        title="Selecione uma conversa"
        description="Escolha uma conversa na lista para ver o histórico, responder e consultar os detalhes operacionais."
      />
    </div>
  }
</div>
